import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Check, Share2, Users, GitBranch, Wallet, Zap, ArrowUpRight, Loader2, Trophy, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, errMsg } from "../lib/api";
import { useAuth } from "../context/Store";
import ProductCard from "../components/ProductCard";
import PartnerWelcomeLetter from "../components/PartnerWelcomeLetter";

function TreeNode({ node, depth = 0, maxDepth = 3, onMemberClick, directPartnerIds = [] }) {
  const children = depth < maxDepth
    ? ["left", "right"].map((side) => {
      const existing = node.children?.find((child) => child.position === side);
      if (existing) return existing;
      const reservedCode = node["reserved_" + side + "_code"] || null;
      const currentParentSlotCode = node.is_reserved_root
        ? node.reservation_code
        : node.open_slot ? node.reservation_parent_slot_code : null;
      return {
        ...(reservedCode ? node["reserved_" + side + "_tree"] || {} : {}),
        name: "Open slot", partner_id: "open-" + node.partner_id + "-" + side, position: side,
        open_slot: true,
        is_reserved_root: Boolean(reservedCode),
        reservation_root_node: node.open_slot ? node.reservation_root_node : node,
        reservation_position: node.open_slot ? node.reservation_position : side,
        reservation_parent_slot_code: currentParentSlotCode,
        reservation_code: reservedCode,
        reserved_count: node["reserved_" + side + "_count"] || 0,
        left_count: "-", right_count: "-", children: [],
      };
    })
    : [];
  const isOpen = Boolean(node.open_slot);
  const isDirect = (Boolean(node.is_direct) || directPartnerIds.includes(node.partner_id)) && !isOpen && !node.is_waiting_parent_activation;
  return (
    <div className="flex flex-col items-center">
      <button type="button"
        onClick={() => (!isOpen && !node.is_reserved_root ? onMemberClick(node) : undefined)}
        className={"rounded-2xl border px-4 py-3 text-center transition " + (isOpen ? (node.reserved_count ? "border-amber-300/60 bg-amber-300/10" : "border-dashed border-white/20 bg-white/[0.02]") : node.is_pending_registration || node.is_reserved_root || node.is_waiting_parent_activation ? "border-amber-300/50 bg-amber-300/[0.06] cursor-pointer hover:border-amber-200" : isDirect ? "border-emerald-300/70 bg-emerald-400/[0.08] shadow-[0_0_24px_rgba(52,211,153,0.16)] hover:border-emerald-200" : "border-brand-cyan/30 bg-brand-surface shadow-[0_0_24px_rgba(0,229,255,0.12)] hover:border-brand-cyan/70")}>
        <p className={"text-sm font-semibold whitespace-nowrap " + (isOpen || node.is_pending_registration || node.is_reserved_root ? "text-amber-100" : "text-white")}>{isOpen && node.reserved_count ? "Reserved" : node.name}</p>
        <p className={"text-[10px] font-mono " + (isOpen || node.is_pending_registration || node.is_reserved_root ? "text-amber-200/70" : isDirect ? "text-emerald-300" : "text-brand-cyan")}>{isOpen ? (node.reserved_count ? "Awaiting activation" : "Available") : node.is_pending_registration ? "Purchase required" : node.is_reserved_root ? "Legacy reservation" : node.partner_id}</p>
        {node.is_reserved_root && <p className="mt-1 text-[8px] font-bold uppercase tracking-[0.18em] text-amber-300">Legacy reservation</p>}
        {node.is_waiting_parent_activation && <p className="mt-1 text-[8px] font-bold uppercase tracking-[0.12em] text-amber-300">Waiting for parent</p>}
        {isDirect && <p className="mt-1 text-[8px] font-bold uppercase tracking-[0.18em] text-emerald-300">Direct member</p>}
        {!isOpen && !node.is_pending_registration && <p className="text-[10px] text-white/40 mt-1">L {node.left_count} · R {node.right_count}</p>}
      </button>
      {children.length > 0 && (
        <>
          <div className="h-6 w-px bg-brand-cyan/30" />
          <div className="flex gap-6 md:gap-10 relative">
            <div className="absolute top-0 left-1/4 right-1/4 h-px bg-brand-cyan/30" />
            {children.map((child) => {
              const reserved = node["reserved_" + child.position + "_count"] || 0;
              return <div key={depth + "-" + node.partner_id + "-" + child.position} className="flex flex-col items-center">
                <div className="h-6 w-px bg-brand-cyan/30" />
                <span className={"text-[9px] uppercase tracking-widest mb-2 text-center " + (child.position === "left" ? "text-brand-cyan" : "text-brand-blue")}>
                  {child.position}{reserved ? " · legacy reserved" : ""}
                </span>
                <TreeNode node={child} depth={depth + 1} maxDepth={maxDepth}
                  onMemberClick={onMemberClick} directPartnerIds={directPartnerIds} />
              </div>;
            })}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-brand-surface/60 p-6 hover:-translate-y-0.5 hover:border-brand-cyan/40 transition-all">
      <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-semibold">{label}</p>
      <p className="font-display text-3xl font-bold text-white mt-2" data-testid={testid}>{value}</p>
      {sub && <p className="text-xs text-brand-cyan mt-1">{sub}</p>}
    </div>
  );
}

const darkInput = "mt-2 w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 text-sm text-white outline-none focus:ring-2 focus:ring-brand-cyan placeholder:text-white/25";

function netWithdrawalBalance(balance, plan) {
  const gross = Number(balance) || 0;
  const chargePct = plan.service_charge_pct ?? plan.charge_pct ?? 0;
  const rebirthPct = plan.rebirth_pct ?? 0;
  return Math.max(0, gross - Math.round(gross * chargePct / 100) - Math.round(gross * rebirthPct / 100));
}

function BankForm({ plan, onSubmit, busy, wd, setWd, testidPrefix, splitNote, availableBalance, kycApproved }) {
  const isRegenx = plan.rebirth_pct != null;
  const netAvailable = netWithdrawalBalance(availableBalance, plan);
  const canWithdraw = isRegenx
    ? availableBalance >= plan.min_withdrawal
    : netAvailable >= plan.min_withdrawal;
  const inputMin = isRegenx ? netWithdrawalBalance(plan.min_withdrawal, plan) : plan.min_withdrawal;
  const inputMax = netAvailable;
  return (
    <form onSubmit={onSubmit} className="rounded-2xl border border-white/10 bg-brand-surface/40 p-7" data-testid={`${testidPrefix}-form`}>
      <h3 className="font-display text-xl font-bold">Request withdrawal</h3>
      <p className="text-xs text-white/40 mt-2">Minimum {isRegenx ? "payout " : ""}{fmt(inputMin)} · {splitNote} · Manual bank transfer after admin verification.</p>
      <div className="mt-5 rounded-xl border border-brand-cyan/20 bg-brand-cyan/5 px-4 py-3" data-testid={`${testidPrefix}-withdrawable-balance`}>
        <p className="text-xs uppercase tracking-wider text-white/45">Withdrawable balance</p>
        <p className="mt-1 font-display text-2xl font-bold text-brand-cyan">{fmt(netAvailable)}</p>
        {!canWithdraw && <p className="mt-1 text-xs text-amber-300">A minimum payout of {fmt(inputMin)} is required to withdraw.</p>}
      </div>
      <div className="mt-6 space-y-4">
        <div><label className="text-xs font-semibold uppercase tracking-wider text-white/40">{isRegenx ? "Bank payout amount (₹)" : "Bank payout amount after 10% deduction (₹)"}</label>
          <input data-testid={`${testidPrefix}-amount`} type="number" min={inputMin} max={inputMax} required disabled={!kycApproved} value={wd.amount} onChange={(e) => setWd({ ...wd, amount: e.target.value })} placeholder={String(inputMin)} className={darkInput + " disabled:cursor-not-allowed disabled:opacity-40"} /></div>
        <div><label className="text-xs font-semibold uppercase tracking-wider text-white/40">Beneficiary name <span className="text-brand-cyan">*</span></label>
          <input data-testid={`${testidPrefix}-holder`} required minLength={2} maxLength={80} disabled={!kycApproved} autoComplete="name" value={wd.account_holder} onChange={(e) => setWd({ ...wd, account_holder: e.target.value })} placeholder="Name as on bank account" className={darkInput + " disabled:cursor-not-allowed disabled:opacity-40"} /></div>
        <div><label className="text-xs font-semibold uppercase tracking-wider text-white/40">Beneficiary account number <span className="text-brand-cyan">*</span></label>
          <input data-testid={`${testidPrefix}-account`} type="text" required minLength={6} maxLength={20} disabled={!kycApproved} autoComplete="off" value={wd.account_number} onChange={(e) => setWd({ ...wd, account_number: e.target.value.trim() })} className={darkInput + " disabled:cursor-not-allowed disabled:opacity-40"} /></div>
        <div><label className="text-xs font-semibold uppercase tracking-wider text-white/40">IFSC code <span className="text-brand-cyan">*</span></label>
          <input data-testid={`${testidPrefix}-ifsc`} required pattern="[A-Za-z]{4}0[A-Za-z0-9]{6}" minLength={11} maxLength={11} disabled={!kycApproved} title="Enter a valid 11-character IFSC code" value={wd.ifsc} onChange={(e) => setWd({ ...wd, ifsc: e.target.value.toUpperCase() })} placeholder="HDFC0001234" className={darkInput + " disabled:cursor-not-allowed disabled:opacity-40"} /></div>
        {!kycApproved && <p className="text-xs text-amber-300">Submit your PAN and wait for KYC approval before requesting a withdrawal.</p>}
        <button type="submit" disabled={busy || !canWithdraw || !kycApproved} data-testid={`${testidPrefix}-submit`}
          className="w-full h-12 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Submit request
        </button>
      </div>
    </form>
  );
}

function KycPanel({ kyc, panNumber, setPanNumber, onSubmit, busy }) {
  const status = kyc?.status || "not_submitted";
  const approved = status === "approved";
  const pending = status === "pending";
  return (
    <section className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6" data-testid="partner-kyc-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-bold">PAN KYC verification</h3>
          <p className="mt-1 text-xs text-white/45">PAN is required and must be approved before withdrawals are enabled.</p>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${approved ? "bg-emerald-400/15 text-emerald-400" : pending ? "bg-amber-400/15 text-amber-300" : status === "rejected" ? "bg-rose-400/15 text-rose-400" : "bg-white/10 text-white/50"}`}>
          {approved ? "Verified" : pending ? "Under review" : status === "rejected" ? "Needs update" : "Not submitted"}
        </span>
      </div>
      {kyc?.pan_masked && <p className="mt-3 text-sm text-white/60">PAN on file: <span className="font-mono text-brand-cyan">{kyc.pan_masked}</span></p>}
      {status === "rejected" && kyc?.review_note && <p className="mt-3 text-xs text-rose-300">Review note: {kyc.review_note}</p>}
      {approved || pending ? (
        <p className="mt-3 text-sm text-white/55">{approved ? "Your PAN has been verified. Withdrawals are enabled." : "Your PAN details are with the admin for review. Withdrawals will be enabled after approval."}</p>
      ) : (
        <form onSubmit={onSubmit} className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-semibold uppercase tracking-wider text-white/45">PAN number *</label>
            <input data-testid="kyc-pan-input" required pattern="[A-Za-z]{5}[0-9]{4}[A-Za-z]" minLength={10} maxLength={10} title="Enter a valid 10-character PAN, for example ABCDE1234F" value={panNumber} onChange={(e) => setPanNumber(e.target.value.toUpperCase())} placeholder="ABCDE1234F" className={darkInput + " font-mono uppercase"} />
          </div>
          <button data-testid="submit-kyc-button" type="submit" disabled={busy} className="h-11 rounded-xl brand-gradient-bg px-5 text-sm font-semibold disabled:opacity-50">{busy ? "Submitting…" : status === "rejected" ? "Resubmit PAN" : "Submit for verification"}</button>
        </form>
      )}
    </section>
  );
}

function WithdrawalHistory({ withdrawals, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-brand-surface/40 p-7" data-testid={testid}>
      <h3 className="font-display text-xl font-bold">Withdrawal history</h3>
      <div className="mt-5 space-y-3">
        {withdrawals.length === 0 && <p className="text-white/30 text-sm py-6 text-center">No withdrawals yet.</p>}
        {withdrawals.map((w) => (
          <div key={w.request_id} className="rounded-xl bg-white/5 px-5 py-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-white/50">{w.request_id}</span>
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${w.status === "paid" ? "bg-emerald-400/15 text-emerald-400" : w.status === "rejected" ? "bg-rose-400/15 text-rose-400" : "bg-amber-400/15 text-amber-400"}`}>{w.status}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-white/60">
              <span>Net {fmt(w.net)} <span className="text-white/30">(gross {fmt(w.gross)})</span></span>
              <span className="text-xs text-white/30">{new Date(w.created_at).toLocaleDateString("en-IN")}</span>
            </div>
            {w.transfer_ref && <p className="mt-1 text-xs text-brand-cyan">Ref: {w.transfer_ref}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Partner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [welcomeLetter, setWelcomeLetter] = useState(null);
  const [showWelcomeLetter, setShowWelcomeLetter] = useState(false);
  const [welcomeAccepting, setWelcomeAccepting] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [tree, setTree] = useState(null);
  const [treeSearch, setTreeSearch] = useState("");
  const [treeModal, setTreeModal] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [referralTokens, setReferralTokens] = useState({});
  const [withdrawals, setWithdrawals] = useState([]);
  const [rs, setRs] = useState(null);
  const [rgx, setRgx] = useState(null);
  const [kits, setKits] = useState([]);
  const [tab, setTab] = useState("overview");
  const [copied, setCopied] = useState("");
  const [wd, setWd] = useState({ amount: "", account_holder: "", account_number: "", ifsc: "" });
  const [rwd, setRwd] = useState({ amount: "", account_holder: "", account_number: "", ifsc: "" });
  const [panNumber, setPanNumber] = useState("");
  const [kycBusy, setKycBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/partner/me").then((r) => {
      setData(r.data);
      if (r.data.is_partner) {
        api.get("/partner/welcome-letter").then((x) => setWelcomeLetter(x.data)).catch(() => {});
        api.get("/partner/ledger").then((x) => setLedger(x.data.ledger)).catch(() => {});
        api.get("/partner/tree").then((x) => { setTree(x.data.tree); setTreeSearch(x.data.tree.partner_id); }).catch(() => {});
        api.get("/partner/referrals").then((x) => setReferrals(x.data.referrals)).catch(() => {});
        api.get("/partner/referral-links").then((x) => setReferralTokens(x.data.links || {})).catch(() => {});
        api.get("/partner/withdrawals").then((x) => setWithdrawals(x.data.withdrawals)).catch(() => {});
        api.get("/partner/rankshare").then((x) => setRs(x.data)).catch(() => {});
        api.get("/partner/regenx").then((x) => setRgx(x.data)).catch(() => {});
      }
    }).catch(() => setData({ is_partner: false }));
    api.get("/products", { params: { activation: true } }).then((r) => setKits(r.data.products)).catch(() => {});
  };

  useEffect(() => {
    if (user === null) navigate("/auth?next=/partner");
    if (user) load();
  }, [user]); // eslint-disable-line

  if (!user || !data) return <div className="pt-40 pb-40 text-center text-brand-ink/40">Loading...</div>;

  if (!data.is_partner) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="become-partner-page">
        <p className="text-xs uppercase tracking-[0.25em] text-brand-blue font-semibold">Vearn Partner Program</p>
        <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-3 max-w-3xl leading-[1.02]">
          Become a <span className="brand-gradient-text">Vearn Partner</span>
        </h1>
        <p className="mt-6 max-w-2xl text-brand-ink/60 leading-relaxed">
          Purchase the Partner Starter Kit to activate your lifetime Partner ID. Share your referral
          code, grow your left and right teams, and earn Direct income of ₹450 per activation plus
          Binary income of ₹300 per matched pair (up to 20 pairs a day). Every kit sale also feeds the
          monthly RankShare pool and places a ReGenX global ID for you. Income is earned on
          qualifying product purchases — never for recruitment alone.
        </p>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {kits.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
        {kits.length === 0 && <p className="mt-10 text-brand-ink/40">Partner kits are being stocked. Check back soon.</p>}
      </div>
    );
  }

  if (!welcomeLetter) return <div className="pt-40 pb-40 text-center text-brand-ink/40">Loading your Partner welcome letter…</div>;

  const acceptWelcomeLetter = async (language) => {
    if (user?.member_view) {
      toast.error("This admin member preview is read-only. Sign in as the Partner to accept the welcome letter.");
      return;
    }
    setWelcomeAccepting(true);
    try {
      const { data: accepted } = await api.post("/partner/welcome-letter/accept", { language });
      setWelcomeLetter((current) => ({ ...current, ...accepted, accepted: true, accepted_language: language }));
      toast.success("Welcome letter accepted. Your Partner dashboard is ready.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setWelcomeAccepting(false);
    }
  };

  if (!welcomeLetter.accepted) {
    return <PartnerWelcomeLetter member={welcomeLetter.member} required onAccept={acceptWelcomeLetter} accepting={welcomeAccepting} readOnly={Boolean(user?.member_view)} />;
  }

  const p = data.partner;
  const referralLinks = ["left", "right"].map((leg) => ({
    leg,
    displayUrl: `${window.location.host}/auth?mode=register&ref=${encodeURIComponent(p.partner_id)}&leg=${leg}`,
    url: `${window.location.origin}/auth?mode=register&ref=${encodeURIComponent(p.partner_id)}&leg=${leg}${referralTokens[leg] ? `&token=${encodeURIComponent(referralTokens[leg])}` : ""}`,
  }));

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  const share = async (url, leg) => {
    try {
      if (navigator.share) await navigator.share({ title: `VEARN ${leg} referral link`, url });
      else copy(url, `${leg}-link`);
    } catch (error) {
      if (error?.name !== "AbortError") toast.error("Could not share this link");
    }
  };

  const loadTreeFor = async (partnerId, extra = {}) => {
    try {
      const { data: result } = await api.get("/partner/tree", { params: { partner_id: partnerId, ...extra } });
      setTree(result.tree);
      setTreeSearch(result.tree.partner_id);
      return result.tree;
    } catch (err) {
      toast.error(errMsg(err));
      return null;
    }
  };

  const openMember = async (member) => {
    if (member.is_pending_registration && member.pending_user_id) {
      try {
        const { data: result } = await api.get("/partner/tree", {
          params: { pending_user_id: member.pending_user_id },
        });
        setTree(result.tree);
        setTreeSearch("");
      } catch (err) {
        toast.error(errMsg(err));
      }
      return;
    }
    const current = await loadTreeFor(member.partner_id);
    setTreeModal({ type: "member", node: current || member });
  };

  const findTree = () => {
    const id = treeSearch.trim().toUpperCase();
    if (!id) return;
    loadTreeFor(id);
  };

  const resetTree = () => {
    setTreeSearch(p.partner_id);
    setTreeModal(null);
    loadTreeFor(p.partner_id);
  };

  const goExtreme = (side) => {
    loadTreeFor(tree?.partner_id || p.partner_id, { extreme: side });
  };

  const submitWd = async (e, endpoint, state, setState) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(endpoint, { ...state, amount: Number(state.amount) });
      toast.success("Withdrawal request submitted for review");
      setState({ amount: "", account_holder: "", account_number: "", ifsc: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitKyc = async (e) => {
    e.preventDefault();
    setKycBusy(true);
    try {
      const response = await api.post("/partner/kyc", { pan_number: panNumber.trim().toUpperCase() });
      setData((current) => ({ ...current, kyc: response.data }));
      setPanNumber("");
      toast.success("PAN submitted for KYC review");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setKycBusy(false);
    }
  };

  const TABS = [
    ["overview", "Income Ledger", Wallet],
    ["network", "My Referrals", Users],
    ["tree", "Binary Tree", GitBranch],
    ["rankshare", "RankShare", Trophy],
    ["regenx", "ReGenX", Globe],
    ["withdraw", "Withdraw", ArrowUpRight],
  ];
  return (
    <div className="min-h-screen bg-brand-navy text-white pt-24 md:pt-28 pb-24" data-testid="partner-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold">Partner Dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3">{p.name}</h1>
            <button type="button" data-testid="view-welcome-letter" onClick={() => setShowWelcomeLetter(true)} className="mt-3 text-sm font-semibold text-brand-cyan hover:text-white">View welcome letter and policies</button>
            <p className="mt-2 text-xs text-white/55">Sponsor: <span className="font-semibold text-white/80">{data.sponsor ? `${data.sponsor.name} · ${data.sponsor.partner_id}` : "No sponsor"}</span></p>
            <p className="text-white/40 text-sm mt-2 font-mono" data-testid="partner-id">{p.partner_id} · {p.email}</p>
          </div>
          <div className="w-full max-w-xl rounded-2xl border border-brand-cyan/25 bg-brand-surface/60 p-5">
            <p className="text-[11px] uppercase tracking-[0.2em] text-white/45 font-semibold">My referral links</p>
            <div className="mt-3 grid gap-3">
              {referralLinks.map(({ leg, url, displayUrl }) => (
                <div key={leg} className="min-w-0 rounded-xl bg-white/[0.04] p-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold capitalize">{leg} referral link</p>
                    <div className="flex shrink-0 gap-2">
                      <button type="button" disabled={!referralTokens[leg]} aria-label={`Copy ${leg} referral link`} onClick={() => copy(url, `${leg}-link`)} className="flex items-center gap-1 rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-white/70 hover:text-white disabled:opacity-40">
                        {copied === `${leg}-link` ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />} Copy
                      </button>
                      <button type="button" disabled={!referralTokens[leg]} aria-label={`Share ${leg} referral link`} onClick={() => share(url, leg)} className="flex items-center gap-1 rounded-lg bg-white/5 px-2.5 py-1.5 text-xs text-white/70 hover:text-white disabled:opacity-40">
                        <Share2 className="h-3.5 w-3.5" /> Share
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[11px] text-white/50" title={displayUrl}>{referralTokens[leg] ? displayUrl : "Preparing secure referral link…"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard label="Withdrawable balance" value={fmt(netWithdrawalBalance(p.wallet_balance, data.plan))} sub={p.wallet_reserved ? `${fmt(p.wallet_reserved)} reserved` : null} testid="wallet-balance" />
          <StatCard label="Total earned" value={fmt(p.total_earned)} sub={`Withdrawn ${fmt(p.total_withdrawn)}`} testid="total-earned" />
          <StatCard label="Active IDs" value={`${data.active_left_count} / ${data.active_right_count}`} sub="Left / Right active members" testid="team-volume" />
          <StatCard label="Binary pairs today" value={`${data.today_pairs_paid} / ${data.plan.daily_pair_limit}`} sub={data.qualified ? "Binary qualified" : "Sponsor 1 left + 1 right to qualify"} testid="pairs-today" />
        </div>

        <div className="mt-10 flex gap-2 overflow-x-auto pb-1">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} data-testid={`partner-tab-${key}`} onClick={() => setTab(key)}
              className={`shrink-0 flex items-center gap-2 rounded-full px-5 h-10 text-sm font-semibold transition-colors ${tab === key ? "brand-gradient-bg text-white" : "bg-white/5 text-white/60 hover:bg-white/10"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "overview" && (
            <>
            <section className="mb-6 rounded-2xl border border-white/10 bg-brand-surface/40 p-6" data-testid="binary-qualification-summary">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-xl font-bold">Binary qualification</h2>
                  <p className={`mt-2 text-sm font-semibold ${data.binary.qualified ? "text-emerald-300" : "text-amber-300"}`}>
                    {data.binary.company_binary_eligible
                      ? "Company Binary Income Enabled — matched pairs can earn Binary Income"
                      : data.binary.qualified
                        ? "Qualified for Binary Income"
                        : `Binary Income Locked — ${data.binary.qualification_reason}`}
                  </p>
                </div>
                <span className="rounded-full bg-white/5 px-3 py-1 text-xs text-white/65">Today: {data.binary.today_paid_pairs} / {data.binary.daily_pair_limit} pairs · {data.binary.today_ceiling_remaining} remaining</span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {[["Personal paid left", data.binary.paid_personal_left_count], ["Personal paid right", data.binary.paid_personal_right_count], ["Total volume L / R", `${data.binary.total_left_volume} / ${data.binary.total_right_volume}`], ["Unmatched carry-forward L / R", `${data.binary.carry_forward_left} / ${data.binary.carry_forward_right}`], ["Matched pairs", data.binary.matched_pairs], ["Expired ceiling pairs", data.binary.expired_pairs], ["Binary income earned", fmt(data.binary.binary_income_earned)]].map(([label, value]) => (
                  <div key={label} className="rounded-xl bg-white/[0.04] p-4"><p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p><p className="mt-1 font-display text-lg font-bold text-white">{value}</p></div>
                ))}
              </div>
            </section>
            <div className="rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto" data-testid="ledger-table">
              <table className="w-full text-sm min-w-[560px]">
                <thead><tr className="text-left text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="px-6 py-4">Date</th><th className="px-6 py-4">Type</th><th className="px-6 py-4">Details</th><th className="px-6 py-4 text-right">Amount</th>
                </tr></thead>
                <tbody>
                  {ledger.length === 0 && <tr><td colSpan={4} className="px-6 py-12 text-center text-white/30">No income yet — share your referral code to begin.</td></tr>}
                  {ledger.map((l) => (
                    <tr key={l.id} className="border-b border-white/5">
                      <td className="px-6 py-4 text-white/50 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("en-IN")}</td>
                      <td className="px-6 py-4"><span className={`rounded-full px-3 py-1 text-xs font-bold ${l.type === "direct" ? "bg-brand-cyan/15 text-brand-cyan" : l.type === "rankshare" ? "bg-amber-400/15 text-amber-400" : "bg-brand-blue/20 text-blue-300"}`}>{l.type}</span></td>
                      <td className="px-6 py-4 text-white/60">{l.note}</td>
                      <td className={`px-6 py-4 text-right font-display font-bold ${l.amount < 0 ? "text-rose-400" : "text-emerald-400"}`}>{l.amount < 0 ? "−" : "+"}{fmt(Math.abs(l.amount))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}

          {tab === "network" && (
            <div className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6" data-testid="referrals-list">
              {referrals.length === 0 ? (
                <p className="text-white/30 text-center py-10">No referrals yet. Share your referral link above.</p>
              ) : (
                <div className="space-y-3">
                  {referrals.map((r) => (
                    <div key={r.id} className="flex items-center justify-between rounded-xl bg-white/5 px-5 py-4">
                      <div>
                        <p className="font-semibold text-sm">{r.name}</p>
                        <p className="text-xs text-white/40">{r.email}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${r.is_partner ? "bg-emerald-400/15 text-emerald-400" : "bg-white/10 text-white/50"}`}>
                        {r.is_partner ? `Partner · ${r.partner_id}` : "Customer"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "tree" && (
            <div className="space-y-6" data-testid="binary-tree">
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-brand-surface/40 p-4">
                <form className="flex flex-1 min-w-64 gap-2" onSubmit={(event) => { event.preventDefault(); findTree(); }}>
                  <input aria-label="Search partner tree by ID" value={treeSearch} onChange={(event) => setTreeSearch(event.target.value.toUpperCase())} placeholder="Search Partner ID" className="h-11 min-w-0 flex-1 rounded-xl bg-brand-navy border border-white/10 px-4 text-sm text-white outline-none focus:ring-2 focus:ring-brand-cyan" />
                  <button className="h-11 rounded-xl border border-white/15 px-4 text-sm">Search</button>
                </form>
                <button type="button" onClick={() => goExtreme("left")} className="h-11 rounded-xl border border-white/15 px-4 text-sm">Extreme left</button>
                <button type="button" onClick={() => goExtreme("right")} className="h-11 rounded-xl border border-white/15 px-4 text-sm">Extreme right</button>
                <button type="button" onClick={resetTree} className="h-11 rounded-xl brand-gradient-bg px-4 text-sm font-semibold">Refresh / top member</button>
              </div>
              <div data-testid="tree-visualization" className="rounded-2xl border border-white/10 bg-brand-surface/40 p-8 overflow-x-auto">
                <div className="mb-4 flex items-center justify-center gap-2 text-xs text-white/55">
                  <span className="h-3 w-3 rounded border border-emerald-300/70 bg-emerald-400/20" />
                  <span>Direct member</span>
                </div>
                <div className="mb-6 text-center"><p className="text-xs text-white/40">Three levels below</p><p className="font-mono font-bold text-brand-cyan">{tree?.partner_id || p.partner_id}</p></div>
                {tree ? <div className="min-w-max mx-auto"><TreeNode node={tree} onMemberClick={openMember} directPartnerIds={referrals.filter((referral) => referral.is_partner && referral.partner_id).map((referral) => referral.partner_id)} /></div> : <p className="text-white/30 text-center py-10">Loading tree...</p>}
              </div>
              {treeModal && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" onClick={() => setTreeModal(null)}>
                <div role="dialog" aria-modal="true" className="w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/15 bg-brand-navy p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-widest text-brand-cyan">Active partner</p>
                      <h3 className="mt-1 font-display text-2xl font-bold">{treeModal.node.name}</h3>
                    </div>
                    <button type="button" onClick={() => setTreeModal(null)} aria-label="Close" className="rounded-full px-3 py-1 text-white/60 hover:bg-white/10">X</button>
                  </div>
                  <div className="mt-4 rounded-xl bg-white/5 p-4 text-sm">
                    <p>Member ID: <span className="font-mono text-brand-cyan">{treeModal.node.partner_id}</span></p>
                    <p className="mt-2">Email: <span className="text-white/65">{treeModal.node.email || "Not available"}</span></p>
                    <p className="mt-2">Phone: <span className="text-white/65">{treeModal.node.phone || "Not available"}</span></p>
                    <button type="button" onClick={() => setTreeModal(null)} className="mt-3 text-xs text-brand-cyan">View this member's tree below</button>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    {["left", "right"].map((side) => (
                      <div key={side} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                        <p className="text-sm font-semibold capitalize">{side} count</p>
                        <p className="mt-2 font-display text-2xl font-bold text-brand-cyan">{treeModal.node[`active_${side}_count`] ?? 0}</p>
                        <p className="mt-1 text-xs text-white/40">Active members</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>}
            </div>
          )}
          {tab === "rankshare" && rs && (
            <div data-testid="rankshare-tab">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard label="Team sales this month" value={fmt(rs.left) + " / " + fmt(rs.right)} sub="Left / Right sales" testid="rs-sales" />
                <StatCard label="Current rank" value={rs.current_rank?.name || "Unranked"} sub={rs.current_rank ? rs.current_rank.shares + " share(s) qualified" : "Not qualified yet"} testid="rs-rank" />
                <StatCard label="Next rank" value={rs.next_rank?.name || "Top rank"} sub={rs.next_rank ? fmt(rs.next_rank.target) + " total sales; weaker leg minimum " + fmt(rs.next_rank.target * rs.next_rank.weaker_pct / 100) : null} testid="rs-next" />
                <StatCard label="Pool this month" value={fmt(rs.pool)} sub={`${rs.pool_kits} eligible kit sales`} testid="rs-pool" />
              </div>
              {rs.next_rank && (
                <div className="mt-6 rounded-2xl border border-white/10 bg-brand-surface/40 p-6">
                  <div className="flex justify-between text-xs text-white/40 mb-2">
                    <span>Progress to {rs.next_rank.name}</span>
                    <span>{fmt(rs.left + rs.right)} / {fmt(rs.next_rank.target)}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full brand-gradient-bg rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, ((rs.left + rs.right) / rs.next_rank.target) * 100)}%` }} data-testid="rs-progress" />
                  </div>
                </div>
              )}
              <div className="mt-6 rounded-2xl border border-white/10 bg-brand-surface/40 p-6">
                <h3 className="font-display text-lg font-bold">Rank ladder</h3>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {rs.ranks.map((r) => (
                    <div key={r.id} className={`rounded-xl border p-5 ${rs.current_rank?.id === r.id ? "border-brand-cyan/50 bg-brand-cyan/5" : "border-white/10 bg-white/5"}`} data-testid={`rank-${r.name.toLowerCase()}`}>
                      <p className="font-display font-bold text-lg flex items-center gap-2"><Trophy className={`h-4 w-4 ${rs.current_rank?.id === r.id ? "text-brand-cyan" : "text-white/30"}`} />{r.name}</p>
                      <p className="text-xs text-white/50 mt-2">{fmt(r.target)} monthly sales; weaker leg minimum {fmt(r.target * r.weaker_pct / 100)}</p>
                      <p className="text-xs text-brand-cyan mt-1 font-semibold">{r.shares} share(s) of the pool</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/30 mt-4">Rs 50 from every eligible kit sale feeds the monthly pool. Shares are paid after month-end finalisation.</p>
              </div>
            </div>
          )}

          {tab === "regenx" && rgx && (
            <div data-testid="regenx-tab">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard label="ReGenX withdrawable" value={fmt(netWithdrawalBalance(rgx.wallet.balance, rgx.plan))} sub={rgx.wallet.reserved ? `${fmt(rgx.wallet.reserved)} reserved` : null} testid="rgx-balance" />
                <StatCard label="Rebirth wallet" value={`${fmt(rgx.wallet.rebirth)} / ₹100`} sub="Auto-creates a new global ID at ₹100" testid="rgx-rebirth" />
                <StatCard label="Global IDs" value={rgx.nodes.length} sub={`${rgx.nodes.filter((n) => n.source === "rebirth").length} from rebirth`} testid="rgx-nodes" />
                <StatCard label="Total ReGenX earned" value={fmt(rgx.wallet.total_earned)} sub={`Withdrawn ${fmt(rgx.wallet.total_withdrawn)}`} testid="rgx-earned" />
              </div>

              <div className="mt-6 rounded-2xl border border-white/10 bg-brand-surface/40 overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead><tr className="text-left text-white/40 text-xs uppercase tracking-wider border-b border-white/10">
                    <th className="px-6 py-4">Global ID</th><th className="px-6 py-4">Level</th><th className="px-6 py-4">Source</th><th className="px-6 py-4">Created</th>
                  </tr></thead>
                  <tbody>
                    {rgx.nodes.length === 0 && <tr><td colSpan={4} className="px-6 py-10 text-center text-white/30">No global IDs yet — a ReGenX ID is created with every Partner kit purchase.</td></tr>}
                    {rgx.nodes.map((n) => (
                      <tr key={n.gid} className="border-b border-white/5" data-testid={`rgx-node-${n.gid}`}>
                        <td className="px-6 py-4 font-mono text-brand-cyan text-xs">{n.gid}</td>
                        <td className="px-6 py-4 text-white/60">Level {n.level}</td>
                        <td className="px-6 py-4"><span className={`rounded-full px-3 py-1 text-xs font-bold ${n.source === "rebirth" ? "bg-brand-blue/20 text-blue-300" : "bg-brand-cyan/15 text-brand-cyan"}`}>{n.source === "admin_activation" ? "Admin activation" : n.source}</span></td>
                        <td className="px-6 py-4 text-white/50">{new Date(n.created_at).toLocaleDateString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl border border-white/10 bg-brand-surface/40 p-6">
                  <h3 className="font-display text-lg font-bold">ReGenX income</h3>
                  <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
                    {rgx.ledger.length === 0 && <p className="text-white/30 text-sm py-6 text-center">Income appears here as new global IDs are placed below yours.</p>}
                    {rgx.ledger.map((l) => (
                      <div key={l.id} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3 text-sm">
                        <span className="text-white/60">Level {l.level} · from <span className="font-mono text-xs text-white/40">{l.from_gid}</span></span>
                        <span className="font-display font-bold text-emerald-400">+{fmt(l.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-6">
                  <KycPanel kyc={data.kyc} panNumber={panNumber} setPanNumber={setPanNumber} onSubmit={submitKyc} busy={kycBusy} />
                  <BankForm plan={rgx.plan} availableBalance={rgx.wallet.balance} busy={busy} wd={rwd} setWd={setRwd} testidPrefix="rgx-wd" kycApproved={data.kyc?.status === "approved"}
                    splitNote="70% bank payout · 20% rebirth wallet"
                    onSubmit={(e) => submitWd(e, "/partner/regenx/withdrawals", rwd, setRwd)} />
                  <WithdrawalHistory withdrawals={rgx.withdrawals} testid="rgx-withdrawal-history" />
                </div>
              </div>
            </div>
          )}

          {tab === "withdraw" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <KycPanel kyc={data.kyc} panNumber={panNumber} setPanNumber={setPanNumber} onSubmit={submitKyc} busy={kycBusy} />
                <BankForm plan={data.plan} availableBalance={p.wallet_balance} busy={busy} wd={wd} setWd={setWd} testidPrefix="wd" kycApproved={data.kyc?.status === "approved"}
                  splitNote={`${data.plan.service_charge_pct}% service charge`}
                  onSubmit={(e) => submitWd(e, "/partner/withdrawals", wd, setWd)} />
              </div>
              <WithdrawalHistory withdrawals={withdrawals} testid="withdrawal-history" />
            </div>
          )}
        </div>
      </div>
      {showWelcomeLetter && <PartnerWelcomeLetter member={welcomeLetter.member} acceptedAt={welcomeLetter.accepted_at} acceptedLanguage={welcomeLetter.accepted_language} onClose={() => setShowWelcomeLetter(false)} />}
    </div>
  );
}
